import React from "react"
import { Link } from "@tanstack/react-router"
import { useAdminDocuments } from "@/lib/queries"
import { Card, CardContent } from "@/components/ui/card"
import { ListChecks, FileText, BookOpen, ChevronRight } from "lucide-react"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { HtmlSectionBody } from "@/components/HtmlSectionBody"


const navCards = [
  { type: "clinical_review", label: "Clinical Reviews", description: "Reference disease-state information", icon: FileText, path: "/admin/clinical-reviews" },
  { type: "workflow_template", label: "Workflows", description: "View your workflows", icon: ListChecks, path: "/admin/workflows" },
  { type: "supplemental", label: "Supplemental", description: "View supplemental resources", icon: BookOpen, path: "/admin/supplemental" },
] as const

const typeConfig: Record<string, { label: string; path: string }> = {
  clinical_review: { label: "Clinical Reviews", path: "/admin/clinical-reviews" },
  workflow_template: { label: "Workflows", path: "/admin/workflows" },
  supplemental: { label: "Supplemental", path: "/admin/supplemental" },
}

export function AdminDashboardPage() {
  const { data: documents = [], isLoading } = useAdminDocuments()

  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  const whatsNewTypes = ["clinical_review", "workflow_template", "supplemental"] as const
  const updatesByType = whatsNewTypes
    .map((type) => {
      const docs = documents
        .filter((d) => d.document_type === type && d.published_at && d.change_summary && new Date(d.published_at) >= thirtyDaysAgo)
        .sort(
          (a, b) =>
            new Date(b.published_at!).getTime() - new Date(a.published_at!).getTime()
        )
      return { type, docs: docs.slice(0, 10) }
    })
    .filter((g) => g.docs.length > 0)

  return (
    <div className="mx-auto w-full max-w-[70%] space-y-8 overflow-auto py-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight text-primary">
          Admin Dashboard
        </h1>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading documents...</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 justify-items-center">
            {navCards.map((card) => {
              const Icon = card.icon
              return (
                <Link key={card.type} to={card.path} className="h-full w-full">
                  <Card className="h-full transition-colors hover:bg-accent">
                    <CardContent className="flex items-center gap-4 p-5">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{card.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {card.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>

          {updatesByType.length > 0 && (
            <div>
              <h2 className="mb-4 text-xl font-semibold text-primary">
                What&apos;s New
              </h2>
              <div className="space-y-5">
                {updatesByType.map(({ type, docs }) => {
                  const config = typeConfig[type]
                  if (!config) return null
                  return (
                    <div key={type}>
                      <h3 className="mb-1 text-base font-semibold text-primary">
                        {config.label}
                      </h3>
                      <div className="space-y-2">
                        {docs.map((doc) => (
                          <Collapsible key={doc.id} defaultOpen={false}>
                            <CollapsibleTrigger className="flex w-full items-center gap-1 text-left text-base text-primary hover:underline group">
                              <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
                              <span>
                                {doc.title}{" "}
                                {doc.admin_only && (
                                  <span className="text-[10px] text-muted-foreground align-middle">Admin</span>
                                )}
                                {" "}
                                <span className="text-sm text-muted-foreground">
                                  ({new Date(doc.published_at!).toLocaleDateString("en-CA", { timeZone: "UTC" })})
                                </span>
                              </span>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="ml-5 mt-1">
                                <HtmlSectionBody html={doc.change_summary} />
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
